import type { AxiosCustomResponse } from '../types.js';
import { objectKeysToCamelCase } from '../utils/objectKeysTo.js';

export function responseDataToCamelCase(
  response: AxiosCustomResponse,
): AxiosCustomResponse {
  const camelCaseBody = response.config.clientOptions?.response?.camelCaseBody;
  if (camelCaseBody === false) {
    return response;
  }

  // Skip binary/typed payloads (Blob, File, ArrayBuffer, FormData, Streams).
  // objectKeysToCamelCase would traverse them via `Object.keys()` and return an
  // empty `{}`, clobbering the payload. Stream endpoints rely on this guard.
  if (!isPlainJsonPayload(response.data)) {
    return response;
  }

  return {
    ...response,
    data: objectKeysToCamelCase(response.data),
  };
}

// Known browser/Node binary containers that must be treated as non-plain:
// objectKeysToCamelCase would traverse them via Object.keys() and clobber them.
const BINARY_PAYLOAD_GUARDS: Array<(data: object) => boolean> = [
  (data) => typeof Blob !== 'undefined' && data instanceof Blob,
  (data) => typeof File !== 'undefined' && data instanceof File,
  (data) => typeof FormData !== 'undefined' && data instanceof FormData,
  (data) =>
    typeof ArrayBuffer !== 'undefined' &&
    (data instanceof ArrayBuffer || ArrayBuffer.isView(data)),
  (data) =>
    typeof ReadableStream !== 'undefined' && data instanceof ReadableStream,
];

function isPlainJsonPayload(data: unknown): boolean {
  if (data == null) return true;
  if (Array.isArray(data)) return true;
  if (typeof data !== 'object') return true;
  return !BINARY_PAYLOAD_GUARDS.some((isBinary) => isBinary(data));
}
